// src/views/admin/inquiries/ContactDetails.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaStickyNote, FaPaperPlane, FaSave } from "react-icons/fa";
import Loader from "../../../component/common/loader";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { InquiryService } from "../../../service/admin/inquiryService";
import { formatDate } from "../../../helper/utils";
import "./style.css";

const statusClassName = (s) => {
  if (!s) return "neutral";
  if (s === "open") return "warning";
  if (s === "responded") return "info";
  if (s === "closed") return "success";
  return "neutral";
};

const InfoItem = ({ label, value, fallback = "-" }) => (
  <div className="dt-info-item">
    <span className="dt-info-label">{label}</span>
    <span className="dt-info-value">{value || fallback}</span>
  </div>
);

const AdminContactDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const message = useMessage();

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [noteAdminName, setNoteAdminName] = useState("");
  const [noteText, setNoteText] = useState("");
  const [statusValue, setStatusValue] = useState("open");
  const [savingStatus, setSavingStatus] = useState(false);
  const notesListRef = useRef(null);

  useEffect(() => {
    if (id) fetchContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchContact = async () => {
    setLoading(true);
    try {
      const res = await InquiryService.getContactById(id);
      if (res?.status?.code === 200) {
        setContact(res.data);
        setStatusValue(res.data.status || "open");
      } else {
        message.showToast("Contact not found", ToastTypes.ERROR);
        navigate("/admin/inquiries");
      }
    } catch (err) {
      message.showToast("Failed to load", ToastTypes.ERROR);
    }
    setLoading(false);
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    setSavingStatus(true);
    try {
      const res = await InquiryService.updateContactStatus(id, { status: statusValue });
      if (res.status.code === 200) {
        setContact(res.data);
        message.showToast("Status updated", ToastTypes.SUCCESS);
      }
    } catch (err) {
      message.showToast("Failed to update", ToastTypes.ERROR);
    }
    setSavingStatus(false);
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return message.showToast("Type reply", ToastTypes.WARNING);
    setReplySending(true);
    try {
      const res = await InquiryService.replyToContact(id, { message: replyText.trim() });
      if (res?.status?.code === 200) {
        setReplyText("");
        setContact(res.data);
        if (res.data?.status) setStatusValue(res.data.status);
        message.showToast("Reply sent", ToastTypes.SUCCESS);
      }
    } catch (err) {
      message.showToast("Failed", ToastTypes.ERROR);
    }
    setReplySending(false);
  };

  const handleSaveNote = async () => {
    if (!noteAdminName.trim()) return message.showToast("Enter admin name");
    if (!noteText.trim()) return message.showToast("Enter note");
    setNotesSaving(true);
    try {
      const res = await InquiryService.addContactNote(id, {
        adminName: noteAdminName.trim(),
        note: noteText.trim(),
      });
      if (res?.status?.code === 200) {
        setContact(res.data);
        setNoteAdminName("");
        setNoteText("");
        setNotesModalOpen(false);
      }
    } finally {
      setNotesSaving(false);
    }
  };

  if (loading) return <Loader />;
  if (!contact) return <p className="dt-empty">Contact not found.</p>;

  const hasNotes = Array.isArray(contact.adminNotes) && contact.adminNotes.length;
  const sortedNotes = hasNotes
    ? [...contact.adminNotes].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    : [];

  return (
    <div className="dt-page">
      {/* ── Top Bar ── */}
      <div className="dt-topbar">
        <button className="dt-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <form className="dt-topbar-center" onSubmit={handleStatusUpdate}>
          <span className="dt-topbar-label">Status</span>
          <select value={statusValue} onChange={(e) => setStatusValue(e.target.value)}>
            <option value="open">Open</option>
            <option value="responded">Responded</option>
            <option value="closed">Closed</option>
          </select>
          <button type="submit" className="dt-btn dt-btn-primary" disabled={savingStatus}>
            <FaSave /> {savingStatus ? "Saving..." : "Update"}
          </button>
        </form>
        <div className="dt-topbar-right">
          <button
            type="button"
            className={`dt-action-btn ${hasNotes ? "dt-action-highlight" : ""}`}
            onClick={() => setNotesModalOpen(true)}
          >
            <FaStickyNote /> {hasNotes ? `Notes (${sortedNotes.length})` : "Add Note"}
          </button>
        </div>
      </div>

      {/* ── Info Grid ── */}
      <div className="dt-grid">
        <div className="dt-card">
          <div className="dt-card-header">
            <h3>User Details</h3>
            <span className="dt-card-badge">{contact.email || ""}</span>
          </div>
          <div className="dt-info-grid">
            <InfoItem label="Name" value={contact.name} />
            <InfoItem label="Phone" value={contact.contactNo} />
            <InfoItem label="Created" value={formatDate(contact.createdAt)} />
          </div>
        </div>

        <div className="dt-card">
          <div className="dt-card-header">
            <h3>Query Details</h3>
            {contact.subject && <span className="dt-card-badge">{contact.subject}</span>}
          </div>
          <div className="dt-info-grid">
            <InfoItem label="Service" value={contact.service} />
            <InfoItem label="Order ID" value={contact.orderId} />
            <InfoItem label="Classification" value={contact.classification} />
            <InfoItem label="Message" value={contact.message} />
          </div>
        </div>
      </div>

      {/* ── Conversation ── */}
      <div className="dt-card">
        <div className="dt-card-header">
          <h3>Conversation</h3>
          <div className="dt-meta-pills">
            <span>Created {formatDate(contact.createdAt)}</span>
            <span>Updated {formatDate(contact.updatedAt)}</span>
          </div>
        </div>
        <div className="dt-chat-feed">
          {Array.isArray(contact.messages) && contact.messages.length ? (
            contact.messages.map((msg, idx) => (
              <div key={`${msg.sentAt || idx}-${idx}`} className={`dt-chat-row ${msg.sender}`}>
                <div className={`dt-chat-bubble ${msg.sender}`}>
                  <div className="dt-chat-meta">
                    <strong>{msg.sender === "admin" ? "You" : contact.name || "User"}</strong>
                    <span>{formatDate(msg.sentAt || contact.createdAt)}</span>
                  </div>
                  <p>{msg.body}</p>
                </div>
              </div>
            ))
          ) : contact.message ? (
            <div className="dt-chat-row user">
              <div className="dt-chat-bubble user">
                <div className="dt-chat-meta">
                  <strong>{contact.name || "User"}</strong>
                  <span>{formatDate(contact.createdAt)}</span>
                </div>
                <p>{contact.message}</p>
              </div>
            </div>
          ) : (
            <p className="dt-empty">No messages yet.</p>
          )}
        </div>
      </div>

      {/* ── Reply ── */}
      <form className="dt-reply-form" onSubmit={handleReply}>
        <label>Reply to user</label>
        <div className="dt-reply-input-row">
          <textarea
            rows={2}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
          />
          <button type="submit" className="dt-btn dt-btn-send" disabled={replySending}>
            <FaPaperPlane /> {replySending ? "Sending..." : "Send"}
          </button>
        </div>
      </form>

      {/* ── Notes Modal ── */}
      {notesModalOpen && (
        <div className="notes-modal-backdrop" onClick={() => !notesSaving && setNotesModalOpen(false)}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-modal-header">
              <div>
                <h3>Admin Notes</h3>
                <p className="notes-modal-subtitle">Internal notes only visible in admin panel.</p>
              </div>
              <button type="button" className="icon-btn" onClick={() => setNotesModalOpen(false)} disabled={notesSaving}>✕</button>
            </div>
            <div className="notes-list" ref={notesListRef}>
              {sortedNotes.length ? (
                sortedNotes.map((note, i) => (
                  <div key={i} className={`note-item ${i === sortedNotes.length - 1 ? "note-latest" : "note-old"}`}>
                    <div className="note-meta">
                      <span className="note-admin">{note.adminName}</span>
                      <span>{formatDate(note.createdAt)}</span>
                    </div>
                    <p className="note-body">{note.note}</p>
                  </div>
                ))
              ) : (
                <p className="dt-empty">No notes yet.</p>
              )}
            </div>
            <div className="notes-form">
              <div className="form-field">
                <label>Admin Name</label>
                <input type="text" value={noteAdminName} onChange={(e) => setNoteAdminName(e.target.value)} />
              </div>
              <div className="form-field">
                <label>Note</label>
                <textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
              </div>
            </div>
            <div className="notes-modal-actions">
              <button type="button" className="ghost-btn" onClick={() => setNotesModalOpen(false)}>Cancel</button>
              <button type="button" className="primary-btn" disabled={notesSaving} onClick={handleSaveNote}>
                {notesSaving ? "Saving..." : "Save Note"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Loader value={loading || replySending} variant="overlay" />
    </div>
  );
};

export default AdminContactDetails;
